import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { Card, EmptyState } from '../../../components/app2';
import { formatMxn } from '../../../utils/billingService';

/**
 * Suscripciones y MRR por plan (rediseño jul-2026).
 *
 * Los datos y los dos ejes son los mismos de siempre; lo que cambia es el
 * lenguaje visual: tarjeta del sistema, paleta del rediseño, tooltip propio
 * (el de recharts trae su fondo blanco fijo y se veía ajeno) y una leyenda
 * escrita a mano para que respete la tipografía.
 */

const BRAND = '#2F6BFF';
const OK = '#2BA779';

const AXIS = { fontSize: 12, stroke: 'var(--muted)', tickLine: false, axisLine: false };
const Y_WIDTH = 44;

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: 'var(--surface)', border: '1px solid var(--line-2)', borderRadius: 10,
        boxShadow: 'var(--shadow)', padding: '10px 12px',
      }}
    >
      <div style={{ font: "700 .82rem 'Hanken Grotesk'", color: 'var(--heading)' }}>{label}</div>
      {payload.map((entry, i) => (
        <div key={i} style={{ font: "500 .82rem 'Hanken Grotesk'", color: entry?.color || 'var(--text)' }}>
          {entry?.dataKey === 'mrr'
            ? `MRR: ${formatMxn(entry?.value)}`
            : `Suscripciones: ${entry?.value}`}
        </div>
      ))}
    </div>
  );
};

const Legend = () => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 12 }}>
    {[
      { color: BRAND, label: 'Suscripciones activas' },
      { color: OK, label: 'MRR (MXN)' },
    ].map((s) => (
      <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
        <span style={{ font: "600 .82rem 'Hanken Grotesk'", color: 'var(--muted)' }}>{s.label}</span>
      </div>
    ))}
  </div>
);

const PlanBreakdownChart = ({ plans = [] }) => {
  const conDatos = plans.filter((p) => (p?.active_count || 0) > 0 || (p?.mrr || 0) > 0);

  return (
    <Card>
      <h3 className="a2-card-title" style={{ fontSize: '1.1rem' }}>Suscripciones por plan</h3>
      <p className="a2-card-sub" style={{ marginBottom: 16 }}>
        Empresas activas e ingreso mensual recurrente de cada tier
      </p>

      {conDatos.length === 0 ? (
        <EmptyState
          icon="bar_chart"
          title="Todavía no hay nada que graficar"
          text="En cuanto una empresa active su plan aparecerá aquí el reparto por tier."
          className="a2-card"
          style={{ border: 'none', padding: '28px 12px' }}
        />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={conDatos} margin={{ top: 8, right: 8, left: 0, bottom: 34 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
              <XAxis dataKey="name" {...AXIS} angle={-22} textAnchor="end" height={56} interval={0} />
              <YAxis yAxisId="left" {...AXIS} allowDecimals={false} width={Y_WIDTH} />
              <YAxis yAxisId="right" orientation="right" {...AXIS} width={Y_WIDTH} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--hover)' }} />
              <Bar yAxisId="left" dataKey="active_count" name="Suscripciones" fill={BRAND} radius={[6, 6, 2, 2]} />
              <Bar yAxisId="right" dataKey="mrr" name="MRR (MXN)" fill={OK} radius={[6, 6, 2, 2]} />
            </BarChart>
          </ResponsiveContainer>
          <Legend />
        </>
      )}
    </Card>
  );
};

export default PlanBreakdownChart;
